import { defaultPieceOf, dedupePieces, type ModulePiece } from "./modulePieces.js";
import {
  ALL_MODULE_SYNERGY_DEFINITIONS,
  type SynergyGrade,
} from "./moduleSynergyDefinitions.js";
import { evaluateModuleEffectStats } from "../modules/ModuleEffect.js";
import { t } from "../i18n/i18n.js";
import { LAUNCHER } from "./balance.js";
export { SYNERGY_CODEX_UNLOCK_CHAPTER } from "./synergyUnlock.js";

/** 도감 전용 표시 순서 — 초반 해금 단순 모듈 → 복잡도 순. 데이터/드랍 순서와 무관. */
const CODEX_DISPLAY_ORDER: readonly string[] = [
  "power", "rapid", "double", "triple", "pierce", "bounce",
  "greed", "mark", "execute", "catalyst", "precision", "shockwave",
  "split", "homing", "explosion", "crit", "amplify", "aftershock",
  "echoPrev", "echoNext", "chain", "burst",
];

const RARITY_RANK: Record<string, number> = {
  common: 0, rare: 1, epic: 2, legendary: 3, transcendent: 4,
};

/** 도감 한 줄 — 정의 1종에 대한 획득/보유 요약 (지시문 §11·§21). */
export type CodexEntryKind = "module" | "shipLauncher";

export interface CodexEntry {
  readonly definitionId: string;
  readonly displayName: string;
  readonly rarity: string;
  readonly kind?: CodexEntryKind;
  readonly nameKey?: string;
  readonly descKey?: string;
  readonly iconKey?: string;
  readonly spaceshipId?: string;
  /** 대표 조각(첫 항목)의 모양 — 기존 단일 심볼 미리보기용(하위호환). */
  readonly shapeId: string;
  /** 이 모듈의 조각 풀 — (shapeId, footprintId) 중복제거. 카드가 조각별 footprint로 그린다(2칸 보존). */
  readonly piecePool: readonly ModulePiece[];
  readonly discovered: boolean;
  readonly ownedCount: number;
  readonly maxLevel: number;
  /** 현재 최고 도달 챕터(bestChapter, 환생 유지) — 카드가 조각별 해금 여부(unlockChapter)를 판정해 잠긴 조각을 숨기고 배지 표시. */
  readonly bestChapter: number;
  /** 마지막 도감 열람(seenChapter) 이후 새로 해금된 조각이 이 모듈에 있는가 — 카드 우상단 레드닷("이게 새로 풀렸어"). */
  readonly newlyUnlocked: boolean;
}

/**
 * 전체 ModuleDefinition을 기준으로 도감 엔트리를 만든다 (지시문 §21). 순수 함수 (테스트 대상).
 * discovered = discoveredIds에 있거나 해당 정의의 인스턴스를 1개 이상 보유. 미발견은 ownedCount/maxLevel 0.
 * bestChapter는 카드가 조각별 해금 표시(잠긴 조각 숨김+자물쇠 배지)에 쓴다 — 기본 Infinity(전부 해금).
 * seenChapter는 카드 우상단 레드닷(seen < unlockChapter <= best인 조각 존재) — 기본 bestChapter(전부 이미 본 것 = 레드닷 없음).
 * 출력 순서 = CODEX_DISPLAY_ORDER 기준 정렬 (도감 전용). 미등록 id는 뒤에서 rarity·입력순 안정 정렬.
 */
export function buildCodexEntries(
  defs: readonly { id: string; displayName: string; rarity: string; pieces: readonly ModulePiece[] }[],
  discoveredIds: ReadonlySet<string>,
  instances: readonly { definitionId: string }[],
  levelOf: (moduleId: string) => number,
  bestChapter = Infinity,
  seenChapter = bestChapter,
): CodexEntry[] {
  const orderIndex = new Map(CODEX_DISPLAY_ORDER.map((id, i) => [id, i]));

  const indexed = defs.map((def, inputIdx) => ({ def, inputIdx }));
  indexed.sort((a, b) => {
    const ai = orderIndex.get(a.def.id);
    const bi = orderIndex.get(b.def.id);
    // 둘 다 알려진 id → 명시 순서
    if (ai !== undefined && bi !== undefined) return ai - bi;
    // 알려진 id 우선
    if (ai !== undefined) return -1;
    if (bi !== undefined) return 1;
    // 둘 다 미등록 → rarity 복잡도 → 원래 입력 순서(안정)
    const ra = RARITY_RANK[a.def.rarity] ?? 99;
    const rb = RARITY_RANK[b.def.rarity] ?? 99;
    return ra !== rb ? ra - rb : a.inputIdx - b.inputIdx;
  });

  return indexed.map(({ def }) => {
    const ownedCount = instances.filter((i) => i.definitionId === def.id).length;
    // maxLevel = moduleId별 플레이어 레벨(환생 유지) — 인스턴스 미보유여도 강화 레벨은 영구 표시.
    const maxLevel = levelOf(def.id);
    return {
      definitionId: def.id,
      displayName: def.displayName,
      rarity: def.rarity,
      kind: "module",
      shapeId: defaultPieceOf(def.pieces).shapeId,
      piecePool: dedupePieces(def.pieces),
      discovered: discoveredIds.has(def.id) || ownedCount > 0,
      ownedCount,
      maxLevel,
      bestChapter,
      // 새 해금: 마지막 열람(seen) 이후 ~ 현재 도달(best) 사이에 해금 경계를 넘은 조각이 하나라도 있으면.
      newlyUnlocked: def.pieces.some((p) => {
        const c = p.unlockChapter ?? 1;
        return c > seenChapter && c <= bestChapter;
      }),
    };
  });
}

export const SPACESHIP_LAUNCHER_CODEX_PREFIX = "spaceshipLauncher:";

export interface SpaceshipLauncherCodexSource {
  readonly id: string;
  readonly launcherNameKey: string;
  readonly launcherDescKey: string;
  readonly launcherIconKey: string;
}

export function spaceshipLauncherCodexId(spaceshipId: string): string {
  return `${SPACESHIP_LAUNCHER_CODEX_PREFIX}${spaceshipId}`;
}

export function spaceshipIdFromLauncherCodexId(definitionId: string): string | null {
  return definitionId.startsWith(SPACESHIP_LAUNCHER_CODEX_PREFIX)
    ? definitionId.slice(SPACESHIP_LAUNCHER_CODEX_PREFIX.length)
    : null;
}

export function buildSpaceshipLauncherCodexEntries(
  spaceships: readonly SpaceshipLauncherCodexSource[],
  ownedSpaceshipIds: readonly string[],
  bestChapter = Infinity,
): CodexEntry[] {
  const owned = new Set(ownedSpaceshipIds);
  const piece: ModulePiece = { shapeId: LAUNCHER.shapeId, footprintId: LAUNCHER.footprintId };
  return spaceships.map((ship) => {
    const discovered = owned.has(ship.id);
    return {
      definitionId: spaceshipLauncherCodexId(ship.id),
      displayName: t(ship.launcherNameKey),
      rarity: "common",
      kind: "shipLauncher",
      spaceshipId: ship.id,
      nameKey: ship.launcherNameKey,
      descKey: ship.launcherDescKey,
      iconKey: ship.launcherIconKey,
      shapeId: LAUNCHER.shapeId,
      piecePool: [piece],
      discovered,
      ownedCount: discovered ? 1 : 0,
      maxLevel: 1,
      bestChapter,
      newlyUnlocked: false,
    };
  });
}

/** 시너지 도감 한 줄 — 항상 공개되는 조건·효과 + 강화 상태. */
export interface SynergyCodexRow {
  readonly id: string;
  readonly grade: SynergyGrade;
  readonly requiredModuleIds: readonly string[];
  readonly level: number;
  readonly nextLevel: number;
  readonly stoneCost: number;
  readonly count: number;
  readonly canUpgrade: boolean;
  readonly name: string;
  readonly detail: string;
}

export interface SynergyPurchasePreview {
  readonly count: number;
  readonly stoneCost: number;
}

/**
 * 시너지 도감 행 목록 — 순수 함수.
 * 시너지는 발견 보상 대상이 아니라 항상 공개된 조합 효과이며, 강화석으로 효과 레벨을 올린다.
 * 출력 순서 = ALL_MODULE_SYNERGY_DEFINITIONS(priority desc) 순서 보존.
 */
export function buildSynergyCodexRows(
  levelOf: (synergyId: string) => number,
  stoneCostOf: (level: number) => number,
  ownedStone: number,
  purchasePreviewOf: (synergyId: string, level: number) => SynergyPurchasePreview = (_id, level) => ({
    count: ownedStone >= stoneCostOf(level) ? 1 : 0,
    stoneCost: stoneCostOf(level),
  }),
): SynergyCodexRow[] {
  return ALL_MODULE_SYNERGY_DEFINITIONS.map((def) => {
    const level = levelOf(def.id);
    const { count, stoneCost } = purchasePreviewOf(def.id, level);
    const displayCount = Math.max(1, count);
    const nextLevel = level + displayCount;
    const condition = def.requiredModuleIds.map((id) => t(`module.${id}.name`)).join(" + ");
    const effect =
      evaluateModuleEffectStats(def.effect, level)
        .map((s) => `${t(`moduleStat.${s.key}`)} ${s.value}`)
        .join(" · ") || "-";
    const nextEffect =
      evaluateModuleEffectStats(def.effect, nextLevel)
        .map((s) => `${t(`moduleStat.${s.key}`)} ${s.value}`)
        .join(" · ") || "-";
    return {
      id: def.id,
      grade: def.grade,
      requiredModuleIds: def.requiredModuleIds,
      level,
      nextLevel,
      stoneCost,
      count,
      canUpgrade: count > 0 && ownedStone >= stoneCost,
      name: t(`synergy.${def.id}.name`),
      detail: t("synergyCodex.detailUpgrade", { condition, effect, nextLevel, nextEffect }),
    };
  });
}

/** 시작 파이프는 드랍/보유 모듈은 아니지만 항상 존재하므로 도감 전용 고정 엔트리로 노출한다. */
export function buildLauncherCodexEntry(bestChapter = Infinity): CodexEntry {
  const piece: ModulePiece = { shapeId: LAUNCHER.shapeId, footprintId: LAUNCHER.footprintId };
  return {
    definitionId: "launcher",
    displayName: t("module.launcher.name"),
    rarity: "common",
    kind: "shipLauncher",
    shapeId: LAUNCHER.shapeId,
    piecePool: [piece],
    discovered: true,
    ownedCount: 1,
    maxLevel: 1,
    bestChapter,
    newlyUnlocked: false,
  };
}
